import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { fetchAndCacheSuppliers } from '../data/suppliers';
import { apiService } from '../services/api.service';

interface Supplier {
  _id: string;
  name: string;
}

interface SupplierSwitcherProps {
  onSupplierChange?: (supplierName: string) => void;
}

const SupplierSwitcher: React.FC<SupplierSwitcherProps> = ({ onSupplierChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Get current supplier from localStorage
    const supplierName = localStorage.getItem('excel_supplier_name');
    if (supplierName) {
      setCurrentSupplier(supplierName);
    }

    // Load suppliers
    loadSuppliers();

    // Close dropdown when clicking outside (works with portal)
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (buttonRef.current && !buttonRef.current.contains(target) && 
          !target.closest('.supplier-switcher-dropdown')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const environment = localStorage.getItem('selectedEnvironment') || 'testing';
      const supplierData = await fetchAndCacheSuppliers(environment);
      setSuppliers(supplierData || []);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      // Fallback to default suppliers if API fails
      setSuppliers([
        { _id: '1', name: 'ASLY' },
        { _id: '2', name: 'SWDY' },
        { _id: '3', name: 'COCO' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 220; // Match CSS width
      
      // Use fixed positioning for better control
      let left = buttonRect.left;
      let top = buttonRect.bottom + 4;
      
      // Adjust if dropdown would go off screen to the right
      if (left + dropdownWidth > window.innerWidth) {
        left = buttonRect.right - dropdownWidth;
      }
      
      // Ensure dropdown doesn't go off screen to the left
      if (left < 10) {
        left = 10;
      }
      
      // Adjust if dropdown would go off screen at the bottom
      if (top + 300 > window.innerHeight) {
        top = buttonRect.top - 304; // Account for dropdown height + margin
      }
      
      setDropdownPosition({ top, left });
    }
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      // Calculate position immediately
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 5,
          left: rect.left
        });
      }
    }
    setIsOpen(!isOpen);
  };

  const handleSupplierSelect = (supplier: Supplier) => {
    try {
      // Update localStorage
      localStorage.setItem('excel_supplier_name', supplier.name);
      
      // Update API service
      apiService.setSupplier(supplier._id);
      
      // Close dropdown
      setIsOpen(false);
      
      // Update local state
      setCurrentSupplier(supplier.name);
      
      // Notify parent component
      if (onSupplierChange) {
        onSupplierChange(supplier.name);
      }
      
      // Show success message and reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
      
    } catch (error) {
      console.error('❌ Failed to switch supplier:', error);
      alert(`Error switching supplier: ${error}`);
    }
  };

  const formatSupplierName = (name: string) => {
    return name.replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  const getCurrentSupplierDisplay = () => {
    if (!currentSupplier) return 'Select Supplier';
    return formatSupplierName(currentSupplier);
  };

  return (
    <div className="supplier-switcher" ref={dropdownRef}>
      <button
        ref={buttonRef}
        className="supplier-switcher-button"
        onClick={toggleDropdown}
        disabled={loading}
        title={`Current: ${getCurrentSupplierDisplay()}`}
      >
        <span className="supplier-switcher-icon">▼</span>
      </button>

      {/* Dropdown with portal rendering */}
      {isOpen && ReactDOM.createPortal(
        <div 
          className="supplier-switcher-dropdown"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          {loading ? (
            <div className="supplier-switcher-loading">Loading...</div>
          ) : (
            <>
              <div className="supplier-switcher-header">
                Current: {getCurrentSupplierDisplay()}
              </div>
              <div className="supplier-switcher-divider"></div>
              {suppliers.map((supplier) => (
                <button
                  key={supplier._id}
                  className={`supplier-switcher-option ${
                    supplier.name === currentSupplier ? 'active' : ''
                  }`}
                  onClick={() => handleSupplierSelect(supplier)}
                >
                  <span className="supplier-option-name">
                    {formatSupplierName(supplier.name)}
                  </span>
                  {supplier.name === currentSupplier && (
                    <span className="supplier-option-check">✓</span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SupplierSwitcher;